import { Alert, Button, Card, Table, Timeline } from "flowbite-react";
import React, { useEffect, useRef, useState } from "react";
import {
  formatDateToDDMM,
  formatToReal,
  isValidCNPJ,
  isValidCPF,
} from "../../utils/utils";
import axios from "../../config/axiosInstance";
import { QUERIES } from "../../config/apiConfig";
import { HiInformationCircle } from "react-icons/hi";
import HeaderQuery from "./HeaderQuery";
import usePDF from "../../hooks/usePDF";
import TitleStripe from "../common/TitleStripe";
import CardQueryDescription from "../common/CardQueryDescription";
import QueryInput from "../inputs/QueryInput";
import SliderToPreview from "../common/SliderToPreview";
import QueryLoading from "../common/QueryLoading";
import AvisoCard from "../common/query/AvisoCard";
import PendenciasFinanceiras from "../common/query/PendenciasFinanceiras";
import NoOcurrence from "../common/query/NoOcurrence";
import Protestos from "../common/query/Protestos";
import ChequesSemFundos from "../common/query/ChequesSemFundos";
import QuadroSocietario from "../common/query/QuadroSocietario";
import FooterQuery from "./FooterQuery";
import moment from "moment";
import { EvaluationScorePJ } from "../common/query/EvaluationScorePJ";
import HistoricoConsultas from "../common/query/HistoricoConsultas";
import { FaPrint } from "react-icons/fa";
import { useReactToPrint } from "react-to-print";
import ChequesSustados from "../common/query/ChequesSustados";
import AcoesCiveis from "../common/query/AcoesCiveis";
import FalenciaRecuperacaoJudicial from "../common/query/FalenciaRecuperacaoJudicial";

const CompletaPlusCNPJQuery = ({
  query,
  selectedPrice,
  dataFromLocalStorage = null,
  hasHeaderPDF = "true",
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [info, setInfo] = useState("");
  const [error, setError] = useState("");
  const [errorData, setErrorData] = useState("");
  const [resumoRetorno, setResumoRetorno] = useState(null);
  const [data, setData] = useState(null);
  const [document, setDocument] = useState("");
  const componentRef = useRef();
  const reactToPrintFn = useReactToPrint({ contentRef: componentRef });

  useEffect(() => {
    if (dataFromLocalStorage) {
      setData(dataFromLocalStorage.consultaCredito);
      setDocument(dataFromLocalStorage?.consultaCredito?.dadosCadastrais?.cpf);
    }
  }, []);

  const fetchInfo = async (type) => {
    return await axios(
      `${QUERIES.GET_V2_QUERY}/completa-plus-cnpj/${info.replace(/\D/g, "")}`
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setDocument(info);
    const unmaskedValue = info.replace(/\D/g, "");
    let isValid = true;
    let type = "F";

    if (unmaskedValue.length <= 11) {
      if (!isValidCPF(unmaskedValue)) {
        setError("CPF inválido!");
        isValid = false;
      }
    } else {
      if (!isValidCNPJ(unmaskedValue)) {
        setError("CNPJ inválido!");
        isValid = false;
      }
      type = "J";
    }

    if (isValid) {
      try {
        setData(null);
        setIsLoading(true);
        setErrorData("");
        const { data: dados } = await fetchInfo(type);
        setData({
          ...dados.data,
          defineLimite:
            dados.data?.defineLimitePositivo || dados.data?.defineLimite,
        });
        setResumoRetorno(dados?.data?.resumoRetorno);
      } catch (error) {
        setErrorData(
          typeof error?.response?.data?.message !== "object"
            ? error?.response?.data?.message
            : "Algum erro ocorreu com sua consulta"
        );
      } finally {
        setIsLoading(false);
      }
    } else {
      setData(null);
    }
  };

  const formatArraySocios = (arraySocios) => {
    return arraySocios?.map((socio) => {
      return {
        nome_empresa: socio?.empresa,
        cpf_cnpj: socio?.cnpj,
        data_entrada: socio?.dataEntrada,
        participacao: socio?.participacaoEmPorcentagem,
      };
    });
  };

  return (
    <>
      {!dataFromLocalStorage && (
        <>
          <CardQueryDescription
            title={query?.name || "Informações da consulta"}
            infos={[
              "Pendências Financeiras",
              "Protestos",
              "Cheques sem Fundos",
              "Cadin",
            ]}
            price={selectedPrice}
          >
            <form
              className="flex flex-col gap-2 bg-white p-2 rounded-lg shadow-md w-fit"
              onSubmit={handleSubmit}
            >
              <div className="flex flex-col md:flex-row gap-2 items-center">
                <QueryInput
                  info={info}
                  setInfo={setInfo}
                  isLoading={isLoading}
                  error={error}
                  type="CNPJ"
                />
                {!errorData && data && (
                  <Button
                    className="bg-gray-600"
                    onClick={() => reactToPrintFn()}
                  >
                    <div className="flex gap-2 items-center">
                      <FaPrint />
                      <div>Imprimir</div>
                    </div>
                  </Button>
                )}
              </div>
            </form>
          </CardQueryDescription>
        </>
      )}

      <SliderToPreview />

      {errorData && (
        <Alert color="failure" icon={HiInformationCircle}>
          <span className="font-medium">{errorData}</span>
        </Alert>
      )}
      {!errorData && (
        <div
          className={`overflow-x-scroll print-content mobile-fit ${
            data || isLoading ? "block" : "hidden"
          }`}
          ref={componentRef}
        >
          {!isLoading ? (
            <Card className="p-5 card-pdf relative overflow-hidden">
              <HeaderQuery
                title={query?.name}
                hasHeader={hasHeaderPDF}
                date={data?.resumoRetorno?.dataConsulta}
              />

              <TitleStripe>Quadro de avisos</TitleStripe>
              <div className="container-alerts three-columns">
                <>
                  <AvisoCard
                    title="Pendências Serasa"
                    hasRestrictions={
                      +data?.crednet?.consultaCredito?.resumoConsulta
                        ?.pendenciasFinanceiras?.quantidadeTotal > 0
                    }
                    messageError={`${data?.crednet?.consultaCredito?.resumoConsulta?.pendenciasFinanceiras?.quantidadeTotal} ocorrências`}
                  />

                  <AvisoCard
                    title="Pendências QUOD"
                    hasRestrictions={
                      +data?.quod?.resumoConsulta?.pendenciasFinanceiras
                        ?.quantidadeTotal > 0
                    }
                    messageError={`${data?.quod?.resumoConsulta?.pendenciasFinanceiras?.quantidadeTotal} ocorrências`}
                  />

                  <AvisoCard
                    title="Cheques sem Fundos"
                    hasRestrictions={
                      !data?.defineLimite?.cheques_sem_fundo?.mensagem
                    }
                    messageError={`${data?.defineLimite?.cheques_sem_fundo?.total_cheques_sem_fundos} 
                                                ocorrências`}
                  />
                  <AvisoCard
                    title="Cheques Sustados"
                    hasRestrictions={
                      !data?.defineLimite?.cheques_sustados?.mensagem
                    }
                    messageError={`${data?.defineLimite?.cheques_sustados?.total} ocorrências`}
                  />
                  <AvisoCard
                    title="Protestos"
                    hasRestrictions={!data?.defineLimite?.protestos?.mensagem}
                    messageError={`${data?.defineLimite?.protestos?.total_protestos} ocorrências`}
                  />
                  <AvisoCard
                    title="Protesto Nacional"
                    hasRestrictions={
                      data?.protestoNacional?.ocorrencias?.length > 0
                    }
                    messageError={`${data?.protestoNacional?.resumoConsulta?.protestos?.valorTotal} ocorrências`}
                  />
                  <AvisoCard
                    title="Negativações"
                    hasRestrictions={
                      !data?.defineLimite?.pendencias_restricoes?.mensagem
                    }
                    messageError={`${data?.defineLimite?.pendencias_restricoes?.total_pendencias}
                                            ocorrências`}
                  />
                  <AvisoCard
                    title="Ações Cíveis"
                    hasRestrictions={
                      !data?.defineLimite?.acoes_civeis?.mensagem
                    }
                    messageError={`${data?.defineLimite?.acoes_civeis?.total_acoes} ocorrências`}
                  />
                  <AvisoCard
                    title="Falência/Recuperação"
                    hasRestrictions={
                      !data?.defineLimite?.falencias_recuperacao_judicial
                        ?.mensagem
                    }
                    messageError={`${data?.defineLimite?.falencias_recuperacao_judicial?.quantidade_total} ocorrências`}
                  />
                </>
              </div>

              <TitleStripe>📄 Dados Cadastrais</TitleStripe>

              <div className="mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-sm p-6">
                <div className="grid grid-cols-2 gap-8 text-sm text-gray-800">
                  {/* Coluna 1 */}
                  <div className="space-y-5">
                    <div>
                      <span className="block text-xs text-gray-500 uppercase tracking-wide mb-1">
                        Razão Social
                      </span>
                      <span className="font-medium">
                        {data?.defineLimite?.identificacao_completo
                          ?.razao_social || "Não informado"}
                      </span>
                    </div>

                    <div>
                      <span className="block text-xs text-gray-500 uppercase tracking-wide mb-1">
                        Natureza Jurídica
                      </span>
                      <span className="font-medium">
                        {data?.defineLimite?.identificacao_completo
                          ?.natureza_juridica || "Não informado"}
                      </span>
                    </div>

                    <div>
                      <span className="block text-xs text-gray-500 uppercase tracking-wide mb-1">
                        CNPJ/MF
                      </span>
                      <span className="font-medium">{document}</span>
                    </div>

                    <div>
                      <span className="block text-xs text-gray-500 uppercase tracking-wide mb-1">
                        CNAE Principal
                      </span>
                      <span className="font-medium">
                        {data?.defineLimite?.identificacao_completo
                          ?.ramo_atividade_primario?.atividade ||
                          "Não informado"}
                      </span>
                    </div>

                    <div>
                      <span className="block text-xs text-gray-500 uppercase tracking-wide mb-1">
                        Fundação
                      </span>
                      <span className="font-medium">
                        {data?.defineLimite?.identificacao_completo
                          ?.data_fundacao || "Não informado"}
                        {data?.defineLimite?.identificacao_completo
                          ?.data_fundacao && (
                          <span className="text-blue-600 font-semibold ml-2">
                            ·{" "}
                            {moment().diff(
                              moment(
                                data?.defineLimite?.identificacao_completo
                                  ?.data_fundacao,
                                "DD/MM/YYYY"
                              ),
                              "years"
                            )}{" "}
                            anos
                          </span>
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Coluna 2 */}
                  <div className="space-y-5">
                    <div>
                      <span className="block text-xs text-gray-500 uppercase tracking-wide mb-1">
                        Endereço
                      </span>
                      <span className="font-medium">
                        {data?.defineLimite?.localizacao_completo?.matriz
                          ?.endereco_matriz || "Sem informação"}
                      </span>
                    </div>

                    <div>
                      <span className="block text-xs text-gray-500 uppercase tracking-wide mb-1">
                        Bairro
                      </span>
                      <span className="font-medium">
                        {data?.defineLimite?.localizacao_completo?.matriz
                          ?.bairro_matriz || "Sem informação"}
                      </span>
                    </div>

                    <div>
                      <span className="block text-xs text-gray-500 uppercase tracking-wide mb-1">
                        Cidade
                      </span>
                      <span className="font-medium">
                        {data?.defineLimite?.localizacao_completo?.matriz
                          ?.cidade_matriz || "Sem informação"}
                      </span>
                    </div>

                    <div>
                      <span className="block text-xs text-gray-500 uppercase tracking-wide mb-1">
                        CEP
                      </span>
                      <span className="font-medium">
                        {data?.defineLimite?.localizacao_completo?.matriz
                          ?.cep_matriz || "Sem informação"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <TitleStripe>Score de risco</TitleStripe>
              <EvaluationScorePJ
                data={data}
                score={data?.defineLimite?.score?.score_pj?.score}
                probability={data?.defineLimite?.score?.score_pj?.probabilidade}
                hideRenda={true}
                hideScore={true}
              />

              <div>
                <TitleStripe className="mb-5">Sócios</TitleStripe>
                {data?.defineLimite?.socios ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm text-left text-gray-700 border border-gray-200">
                      <thead className="bg-gray-100 text-xs uppercase font-semibold text-gray-600">
                        <tr>
                          <th className="px-3 py-2">Nome</th>
                          <th className="px-3 py-2">Part.</th>
                          <th className="px-3 py-2">Assina</th>
                          <th className="px-3 py-2">Fraude</th>
                          <th className="px-3 py-2">Situação</th>
                          <th className="px-3 py-2">Restrições</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(Array.isArray(data?.defineLimite?.socios?.socio)
                          ? data?.defineLimite?.socios?.socio
                          : [data?.defineLimite?.socios?.socio]
                        ).map((socio, index) => {
                          const restricoes = socio?.restricoes_documento || {};
                          return (
                            <tr
                              key={index}
                              className={
                                index % 2 === 0 ? "bg-white" : "bg-gray-50"
                              }
                            >
                              <td className="px-3 py-2">
                                <div>{socio?.nome_empresa}</div>
                                <div className="text-xs">
                                  Doc.: {socio?.cpf_cnpj}
                                </div>
                              </td>
                              <td className="px-3 py-2">
                                {socio?.participacao}%
                              </td>
                              <td className="px-3 py-2">
                                {socio?.assina_empresa === "S" ? "Sim" : "Não"}
                              </td>
                              <td className="px-3 py-2">
                                {socio?.indicador_fraude}
                              </td>
                              <td className="px-3 py-2">
                                {socio?.situacao_documento}
                              </td>
                              <td className="px-3 py-2 text-xs leading-5">
                                <div>
                                  🛑 Pend.:{" "}
                                  {restricoes?.pendencias_restricoes
                                    ?.quantidade || 0}
                                </div>
                                <div>
                                  📄 Prot.:{" "}
                                  {restricoes?.protestos?.quantidade || 0}
                                </div>
                                <div>
                                  💸 Cheques:{" "}
                                  {restricoes?.cheques_sem_fundo?.quantidade ||
                                    0}
                                </div>
                                <div>
                                  ⚖️ Ações:{" "}
                                  {restricoes?.acoes_judiciais?.quantidade || 0}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <NoOcurrence />
                )}
              </div>

              <div>
                <TitleStripe className="mb-5">
                  {" "}
                  Participações dos Sócios e Administradores em Outras Empresas
                </TitleStripe>
                {!data?.defineLimite?.participacoes_socios_adm_outras_empresas
                  ?.mensagem ? (
                  <>
                    {Array.isArray(
                      data?.defineLimite
                        ?.participacoes_socios_adm_outras_empresas?.socio
                    ) ? (
                      <>
                        {data?.defineLimite?.participacoes_socios_adm_outras_empresas?.socio?.map(
                          (socioP, index) => (
                            <div key={index} className="mt-10">
                              <div className="font-semibold mb-1 text-blue-600 text-center underline">
                                {socioP.nome_socio}
                              </div>

                              <div className="overflow-x-auto mt-3 mb-5 rounded-lg border border-gray-300">
                                <Table striped>
                                  <Table.Head className="text-black">
                                    <Table.HeadCell
                                      className="text-center py-2 px-1"
                                      style={{ background: "#EBF5FF" }}
                                    >
                                      Nº
                                    </Table.HeadCell>
                                    <Table.HeadCell
                                      className="py-2"
                                      style={{ background: "#EBF5FF" }}
                                    >
                                      Denominação Social
                                    </Table.HeadCell>
                                    <Table.HeadCell
                                      className="text-center py-2 px-1"
                                      style={{ background: "#EBF5FF" }}
                                    >
                                      Data <br /> Entrada
                                    </Table.HeadCell>
                                    <Table.HeadCell
                                      className="text-center py-2 px-1"
                                      style={{ background: "#EBF5FF" }}
                                    >
                                      Part. (%)
                                    </Table.HeadCell>
                                    <Table.HeadCell
                                      className="text-center py-2 px-1"
                                      style={{ background: "#EBF5FF" }}
                                    >
                                      Situação
                                    </Table.HeadCell>
                                    <Table.HeadCell
                                      className="py-2"
                                      style={{ background: "#EBF5FF" }}
                                    >
                                      Pendências
                                    </Table.HeadCell>
                                  </Table.Head>

                                  <Table.Body className="divide-y">
                                    {Array.isArray(
                                      socioP?.socios_participantes
                                    ) ? (
                                      <>
                                        {socioP?.socios_participantes?.map(
                                          (socio, index) => (
                                            <Table.Row
                                              key={index}
                                              className="bg-white dark:border-gray-700 dark:bg-gray-800 text-black"
                                            >
                                              <Table.Cell className="font-medium dark:text-white text-center py-1 px-1">
                                                {index + 1}
                                              </Table.Cell>
                                              <Table.Cell className=" py-1 w-96">
                                                <div>{socio?.razao_social}</div>
                                                <div
                                                  style={{ fontSize: "12px" }}
                                                >
                                                  CNPJ: {socio?.cnpj}
                                                </div>
                                              </Table.Cell>
                                              <Table.Cell className="text-center py-1 px-1">
                                                {socio?.entrada}
                                              </Table.Cell>
                                              <Table.Cell className="text-center py-1 px-1 px-1">
                                                {socio?.participacao}
                                              </Table.Cell>
                                              <Table.Cell className="text-center py-1 w-24">
                                                {socio?.situacao_cnpj}
                                              </Table.Cell>
                                              <Table.Cell className="py-1 text-2xs text-black">
                                                {socio?.indicador_debito?.toUpperCase() ===
                                                "SIM" ? (
                                                  <>
                                                    {socio?.restricoes_documento
                                                      ?.cheques_sem_fundo
                                                      ?.quantidade !== "0" && (
                                                      <div className="uppercase">
                                                        Cheques Sem Fundos:{" "}
                                                        <span className="text-red-500 font-semibold">
                                                          {
                                                            socio
                                                              ?.restricoes_documento
                                                              ?.cheques_sem_fundo
                                                              ?.quantidade
                                                          }{" "}
                                                          ocorrências
                                                        </span>
                                                      </div>
                                                    )}

                                                    {socio?.restricoes_documento
                                                      ?.protestos
                                                      ?.quantidade !== "0" && (
                                                      <div className="uppercase">
                                                        Protestos:{" "}
                                                        <span className="text-red-500 font-semibold">
                                                          {formatToReal(
                                                            socio
                                                              ?.restricoes_documento
                                                              ?.protestos?.valor
                                                          )}
                                                        </span>
                                                      </div>
                                                    )}

                                                    {socio?.restricoes_documento
                                                      ?.pendencias_restricoes
                                                      ?.quantidade !== "0" && (
                                                      <div className="uppercase">
                                                        Pendências:{" "}
                                                        <span className="text-red-500 font-semibold">
                                                          {formatToReal(
                                                            socio
                                                              ?.restricoes_documento
                                                              ?.pendencias_restricoes
                                                              ?.valor
                                                          )}
                                                        </span>
                                                      </div>
                                                    )}

                                                    {socio?.restricoes_documento
                                                      ?.acoes_judiciais
                                                      ?.quantidade !== "0" && (
                                                      <div className="uppercase">
                                                        Ações cíveis:{" "}
                                                        <span className="text-red-500 font-semibold">
                                                          {formatToReal(
                                                            socio
                                                              ?.restricoes_documento
                                                              ?.acoes_judiciais
                                                              ?.valor
                                                          )}
                                                        </span>
                                                      </div>
                                                    )}

                                                    {socio?.restricoes_documento
                                                      ?.falencias_recuperacao_judicial
                                                      ?.quantidade !== "0" && (
                                                      <div className="uppercase">
                                                        Rec. Judicial/Falências:{" "}
                                                        <span className="text-red-500 font-semibold">
                                                          {formatToReal(
                                                            socio
                                                              ?.restricoes_documento
                                                              ?.falencias_recuperacao_judicial
                                                              ?.valor
                                                          )}
                                                        </span>
                                                      </div>
                                                    )}
                                                  </>
                                                ) : (
                                                  <div className="text-blue-500">
                                                    Não há débitos
                                                  </div>
                                                )}
                                              </Table.Cell>
                                            </Table.Row>
                                          )
                                        )}
                                      </>
                                    ) : (
                                      <>
                                        <Table.Row className="bg-white dark:border-gray-700 dark:bg-gray-800 text-black">
                                          <Table.Cell className="font-medium dark:text-white text-center py-1 px-1">
                                            {1}
                                          </Table.Cell>
                                          <Table.Cell className=" py-1 w-96">
                                            <div>
                                              {
                                                socioP?.socios_participantes
                                                  ?.razao_social
                                              }
                                            </div>
                                            <div style={{ fontSize: "12px" }}>
                                              CNPJ:{" "}
                                              {
                                                socioP?.socios_participantes
                                                  ?.cnpj
                                              }
                                            </div>
                                          </Table.Cell>
                                          <Table.Cell className="text-center py-1 px-1">
                                            {
                                              socioP?.socios_participantes
                                                ?.entrada
                                            }
                                          </Table.Cell>
                                          <Table.Cell className="text-center py-1 px-1 px-1">
                                            {
                                              socioP?.socios_participantes
                                                ?.participacao
                                            }
                                          </Table.Cell>
                                          <Table.Cell className="text-center py-1 w-24">
                                            {
                                              socioP?.socios_participantes
                                                ?.situacao_cnpj
                                            }
                                          </Table.Cell>
                                          <Table.Cell className="py-1 text-2xs text-black">
                                            {socioP?.socios_participantes?.indicador_debito?.toUpperCase() ===
                                            "SIM" ? (
                                              <>
                                                {socioP?.socios_participantes
                                                  ?.restricoes_documento
                                                  ?.cheques_sem_fundo
                                                  ?.quantidade !== "0" && (
                                                  <div className="uppercase">
                                                    Cheques Sem Fundos:{" "}
                                                    <span className="text-red-500 font-semibold">
                                                      {
                                                        socioP
                                                          ?.socios_participantes
                                                          ?.restricoes_documento
                                                          ?.cheques_sem_fundo
                                                          ?.quantidade
                                                      }{" "}
                                                      ocorrências
                                                    </span>
                                                  </div>
                                                )}

                                                {socioP?.socios_participantes
                                                  ?.restricoes_documento
                                                  ?.protestos?.quantidade !==
                                                  "0" && (
                                                  <div className="uppercase">
                                                    Protestos:{" "}
                                                    <span className="text-red-500 font-semibold">
                                                      {formatToReal(
                                                        socioP
                                                          ?.socios_participantes
                                                          ?.restricoes_documento
                                                          ?.protestos?.valor
                                                      )}
                                                    </span>
                                                  </div>
                                                )}

                                                {socioP?.socios_participantes
                                                  ?.restricoes_documento
                                                  ?.pendencias_restricoes
                                                  ?.quantidade !== "0" && (
                                                  <div className="uppercase">
                                                    Pendências:{" "}
                                                    <span className="text-red-500 font-semibold">
                                                      {formatToReal(
                                                        socioP
                                                          ?.socios_participantes
                                                          ?.restricoes_documento
                                                          ?.pendencias_restricoes
                                                          ?.valor
                                                      )}
                                                    </span>
                                                  </div>
                                                )}

                                                {socioP?.socios_participantes
                                                  ?.restricoes_documento
                                                  ?.acoes_judiciais
                                                  ?.quantidade !== "0" && (
                                                  <div className="uppercase">
                                                    Ações cíveis:{" "}
                                                    <span className="text-red-500 font-semibold">
                                                      {formatToReal(
                                                        socioP
                                                          ?.socios_participantes
                                                          ?.restricoes_documento
                                                          ?.acoes_judiciais
                                                          ?.valor
                                                      )}
                                                    </span>
                                                  </div>
                                                )}

                                                {socioP?.socios_participantes
                                                  ?.restricoes_documento
                                                  ?.falencias_recuperacao_judicial
                                                  ?.quantidade !== "0" && (
                                                  <div className="uppercase">
                                                    Rec. Judicial/Falências:{" "}
                                                    <span className="text-red-500 font-semibold">
                                                      {formatToReal(
                                                        socioP
                                                          ?.socios_participantes
                                                          ?.restricoes_documento
                                                          ?.falencias_recuperacao_judicial
                                                          ?.valor
                                                      )}
                                                    </span>
                                                  </div>
                                                )}
                                              </>
                                            ) : (
                                              <div className="text-blue-500">
                                                Não há débitos
                                              </div>
                                            )}
                                          </Table.Cell>
                                        </Table.Row>
                                      </>
                                    )}
                                  </Table.Body>
                                </Table>
                              </div>
                            </div>
                          )
                        )}
                      </>
                    ) : (
                      <>
                        <div className="mt-10">
                          <div className="font-semibold mb-1 text-blue-600 text-center underline">
                            {
                              data?.defineLimite
                                ?.participacoes_socios_adm_outras_empresas
                                ?.socio.nome_socio
                            }
                          </div>

                          <div className="overflow-x-auto mt-3 mb-5 rounded-lg border border-gray-300">
                            <Table striped>
                              <Table.Head className="text-black">
                                <Table.HeadCell
                                  className="text-center py-2 px-1"
                                  style={{ background: "#EBF5FF" }}
                                >
                                  Nº
                                </Table.HeadCell>
                                <Table.HeadCell
                                  className="py-2"
                                  style={{ background: "#EBF5FF" }}
                                >
                                  Denominação Social
                                </Table.HeadCell>
                                <Table.HeadCell
                                  className="text-center py-2 px-1"
                                  style={{ background: "#EBF5FF" }}
                                >
                                  Data <br /> Entrada
                                </Table.HeadCell>
                                <Table.HeadCell
                                  className="text-center py-2 px-1"
                                  style={{ background: "#EBF5FF" }}
                                >
                                  Part. (%)
                                </Table.HeadCell>
                                <Table.HeadCell
                                  className="text-center py-2 px-1"
                                  style={{ background: "#EBF5FF" }}
                                >
                                  Situação
                                </Table.HeadCell>
                                <Table.HeadCell
                                  className="py-2"
                                  style={{ background: "#EBF5FF" }}
                                >
                                  Pendências
                                </Table.HeadCell>
                              </Table.Head>

                              <Table.Body className="divide-y">
                                {Array.isArray(
                                  data?.defineLimite
                                    ?.participacoes_socios_adm_outras_empresas
                                    ?.socio?.socios_participantes
                                ) ? (
                                  <>
                                    {data?.defineLimite?.participacoes_socios_adm_outras_empresas?.socio?.socios_participantes?.map(
                                      (socio, index) => (
                                        <Table.Row
                                          key={index}
                                          className="bg-white dark:border-gray-700 dark:bg-gray-800 text-black"
                                        >
                                          <Table.Cell className="font-medium dark:text-white text-center py-1 px-1">
                                            {index + 1}
                                          </Table.Cell>
                                          <Table.Cell className=" py-1 w-96">
                                            <div>{socio.razao_social}</div>
                                            <div style={{ fontSize: "12px" }}>
                                              CNPJ: {socio.cnpj}
                                            </div>
                                          </Table.Cell>
                                          <Table.Cell className="text-center py-1 px-1">
                                            {socio.entrada}
                                          </Table.Cell>
                                          <Table.Cell className="text-center py-1 px-1 px-1">
                                            {socio.participacao}
                                          </Table.Cell>
                                          <Table.Cell className="text-center py-1 w-24">
                                            {socio.situacao_cnpj}
                                          </Table.Cell>
                                          <Table.Cell className="py-1 text-2xs text-black">
                                            {socio?.indicador_debito?.toUpperCase() ===
                                            "SIM" ? (
                                              <>
                                                {socio?.restricoes_documento
                                                  ?.cheques_sem_fundo
                                                  ?.quantidade !== "0" && (
                                                  <div className="uppercase">
                                                    Cheques Sem Fundos:{" "}
                                                    <span className="text-red-500 font-semibold">
                                                      {
                                                        socio
                                                          ?.restricoes_documento
                                                          ?.cheques_sem_fundo
                                                          ?.quantidade
                                                      }{" "}
                                                      ocorrências
                                                    </span>
                                                  </div>
                                                )}

                                                {socio?.restricoes_documento
                                                  ?.protestos?.quantidade !==
                                                  "0" && (
                                                  <div className="uppercase">
                                                    Protestos:{" "}
                                                    <span className="text-red-500 font-semibold">
                                                      {formatToReal(
                                                        socio
                                                          ?.restricoes_documento
                                                          ?.protestos?.valor
                                                      )}
                                                    </span>
                                                  </div>
                                                )}

                                                {socio?.restricoes_documento
                                                  ?.pendencias_restricoes
                                                  ?.quantidade !== "0" && (
                                                  <div className="uppercase">
                                                    Pendências:{" "}
                                                    <span className="text-red-500 font-semibold">
                                                      {formatToReal(
                                                        socio
                                                          ?.restricoes_documento
                                                          ?.pendencias_restricoes
                                                          ?.valor
                                                      )}
                                                    </span>
                                                  </div>
                                                )}

                                                {socio?.restricoes_documento
                                                  ?.acoes_judiciais
                                                  ?.quantidade !== "0" && (
                                                  <div className="uppercase">
                                                    Ações cíveis:{" "}
                                                    <span className="text-red-500 font-semibold">
                                                      {formatToReal(
                                                        socio
                                                          ?.restricoes_documento
                                                          ?.acoes_judiciais
                                                          ?.valor
                                                      )}
                                                    </span>
                                                  </div>
                                                )}

                                                {socio?.restricoes_documento
                                                  ?.falencias_recuperacao_judicial
                                                  ?.quantidade !== "0" && (
                                                  <div className="uppercase">
                                                    Rec. Judicial/Falências:{" "}
                                                    <span className="text-red-500 font-semibold">
                                                      {formatToReal(
                                                        socio
                                                          ?.restricoes_documento
                                                          ?.falencias_recuperacao_judicial
                                                          ?.valor
                                                      )}
                                                    </span>
                                                  </div>
                                                )}
                                              </>
                                            ) : (
                                              <div className="text-blue-500">
                                                Não há débitos
                                              </div>
                                            )}
                                          </Table.Cell>
                                        </Table.Row>
                                      )
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <Table.Row className="bg-white dark:border-gray-700 dark:bg-gray-800 text-black">
                                      <Table.Cell className="font-medium dark:text-white text-center py-1 px-1">
                                        {1}
                                      </Table.Cell>
                                      <Table.Cell className=" py-1 w-96">
                                        <div>
                                          {
                                            data
                                              ?.participacoes_socios_adm_outras_empresas
                                              ?.socio?.socios_participantes
                                              ?.razao_social
                                          }
                                        </div>
                                        <div style={{ fontSize: "10px" }}>
                                          CNPJ:{" "}
                                          {
                                            data
                                              ?.participacoes_socios_adm_outras_empresas
                                              ?.socio?.socios_participantes
                                              ?.cnpj
                                          }
                                        </div>
                                      </Table.Cell>
                                      <Table.Cell className="text-center py-1 px-1">
                                        {
                                          data
                                            ?.participacoes_socios_adm_outras_empresas
                                            ?.socio?.socios_participantes
                                            ?.entrada
                                        }
                                      </Table.Cell>
                                      <Table.Cell className="text-center py-1 px-1 px-1">
                                        {
                                          data
                                            ?.participacoes_socios_adm_outras_empresas
                                            ?.socio?.socios_participantes
                                            ?.participacao
                                        }
                                      </Table.Cell>
                                      <Table.Cell className="text-center py-1 w-24">
                                        {
                                          data
                                            ?.participacoes_socios_adm_outras_empresas
                                            ?.socio?.socios_participantes
                                            ?.situacao_cnpj
                                        }
                                      </Table.Cell>
                                      <Table.Cell className="py-1 text-2xs text-black">
                                        {data?.participacoes_socios_adm_outras_empresas?.socio?.socios_participantes.indicador_debito?.toUpperCase() ===
                                        "SIM" ? (
                                          <>
                                            {data
                                              ?.participacoes_socios_adm_outras_empresas
                                              ?.socio?.socios_participantes
                                              ?.restricoes_documento
                                              ?.cheques_sem_fundo
                                              ?.quantidade !== "0" && (
                                              <div className="uppercase">
                                                Cheques Sem Fundos:{" "}
                                                <span className="text-red-500 font-semibold">
                                                  {
                                                    data
                                                      ?.participacoes_socios_adm_outras_empresas
                                                      ?.socio
                                                      ?.socios_participantes
                                                      ?.restricoes_documento
                                                      ?.cheques_sem_fundo
                                                      ?.quantidade
                                                  }
                                                </span>
                                              </div>
                                            )}

                                            {data
                                              ?.participacoes_socios_adm_outras_empresas
                                              ?.socio?.socios_participantes
                                              ?.restricoes_documento?.protestos
                                              ?.quantidade !== "0" && (
                                              <div className="uppercase">
                                                Protestos:{" "}
                                                <span className="text-red-500 font-semibold">
                                                  {formatToReal(
                                                    data
                                                      ?.participacoes_socios_adm_outras_empresas
                                                      ?.socio
                                                      ?.socios_participantes
                                                      ?.restricoes_documento
                                                      ?.protestos?.valor
                                                  )}
                                                </span>
                                              </div>
                                            )}

                                            {data
                                              ?.participacoes_socios_adm_outras_empresas
                                              ?.socio?.socios_participantes
                                              ?.restricoes_documento
                                              ?.pendencias_restricoes
                                              ?.quantidade !== "0" && (
                                              <div className="uppercase">
                                                Pendências:{" "}
                                                <span className="text-red-500 font-semibold">
                                                  {formatToReal(
                                                    data
                                                      ?.participacoes_socios_adm_outras_empresas
                                                      ?.socio
                                                      ?.socios_participantes
                                                      .restricoes_documento
                                                      ?.pendencias_restricoes
                                                      ?.valor
                                                  )}
                                                </span>
                                              </div>
                                            )}

                                            {data
                                              ?.participacoes_socios_adm_outras_empresas
                                              ?.socio?.socios_participantes
                                              ?.restricoes_documento
                                              ?.acoes_judiciais?.quantidade !==
                                              "0" && (
                                              <div className="uppercase">
                                                Ações cíveis:{" "}
                                                <span className="text-red-500 font-semibold">
                                                  {formatToReal(
                                                    data
                                                      ?.participacoes_socios_adm_outras_empresas
                                                      ?.socio
                                                      ?.socios_participantes
                                                      ?.restricoes_documento
                                                      ?.acoes_judiciais?.valor
                                                  )}
                                                </span>
                                              </div>
                                            )}

                                            {data
                                              ?.participacoes_socios_adm_outras_empresas
                                              ?.socio?.socios_participantes
                                              ?.restricoes_documento
                                              ?.falencias_recuperacao_judicial
                                              ?.quantidade !== "0" && (
                                              <div className="uppercase">
                                                Rec. Judicial/Falências:{" "}
                                                <span className="text-red-500 font-semibold">
                                                  {formatToReal(
                                                    data
                                                      ?.participacoes_socios_adm_outras_empresas
                                                      ?.socio
                                                      ?.socios_participantes
                                                      ?.restricoes_documento
                                                      ?.falencias_recuperacao_judicial
                                                      ?.valor
                                                  )}
                                                </span>
                                              </div>
                                            )}
                                          </>
                                        ) : (
                                          <div className="text-blue-500">
                                            Não há débitos
                                          </div>
                                        )}
                                      </Table.Cell>
                                    </Table.Row>
                                  </>
                                )}
                              </Table.Body>
                            </Table>
                          </div>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <NoOcurrence />
                )}
              </div>

              <div>
                <TitleStripe className="mb-5">Cheques Sem Fundos</TitleStripe>
                {!!data?.defineLimite?.cheques_sem_fundo
                  ?.total_cheques_sem_fundos ? (
                  <>
                    <ChequesSemFundos
                      quantidadeTotal={
                        data?.defineLimite?.cheques_sem_fundo
                          ?.total_cheques_sem_fundos
                      }
                      totalNomes={
                        data?.defineLimite?.cheques_sem_fundo
                          ?.total_nomes_relacionados
                      }
                      chequesSemFundos={
                        Array.isArray(
                          data?.defineLimite?.cheques_sem_fundo
                            ?.ultimas_ocorrencias?.cheque_sem_fundo
                        )
                          ? data?.defineLimite?.cheques_sem_fundo
                              ?.ultimas_ocorrencias?.cheque_sem_fundo
                          : [
                              ...[],
                              data?.defineLimite?.cheques_sem_fundo
                                ?.ultimas_ocorrencias?.cheque_sem_fundo,
                            ]
                      }
                    />
                  </>
                ) : (
                  <NoOcurrence />
                )}
              </div>
              <div>
                <TitleStripe className="mb-5">Cheques Sustados</TitleStripe>
                {data?.defineLimite?.cheques_sustados?.ultimas_ocorrencias
                  ?.cheques_sustado?.length > 0 ? (
                  <>
                    <ChequesSustados
                      periodoInicial={
                        data?.defineLimite?.cheques_sustados?.periodo_inicial
                      }
                      periodoFinal={
                        data?.defineLimite?.cheques_sustados?.periodo_final
                      }
                      quantidadeTotal={
                        data?.defineLimite?.cheques_sustados?.total
                      }
                      chequesSustados={
                        Array.isArray(
                          data?.defineLimite?.cheques_sustados
                            ?.ultimas_ocorrencias?.cheques_sustado
                        )
                          ? data?.defineLimite?.cheques_sustados
                              ?.ultimas_ocorrencias?.cheques_sustado
                          : [
                              ...[],
                              data?.defineLimite?.cheques_sustados
                                ?.ultimas_ocorrencias?.cheques_sustado,
                            ]
                      }
                    />
                  </>
                ) : (
                  <NoOcurrence />
                )}
              </div>

              <div>
                <TitleStripe className="mb-5">Protestos</TitleStripe>
                {!!data?.defineLimite?.protestos?.total_protestos ? (
                  <Protestos
                    quantidadeTotal={
                      data?.defineLimite?.protestos?.total_protestos
                    }
                    valorTotal={data?.defineLimite?.protestos?.valor_total}
                    primeiroValor={
                      data?.defineLimite?.protestos?.valor_primeiro
                    }
                    protestos={
                      Array.isArray(
                        data?.defineLimite?.protestos?.ultimas_ocorrencias
                          ?.protesto
                      )
                        ? data?.defineLimite?.protestos?.ultimas_ocorrencias
                            ?.protesto
                        : [
                            ...[],
                            data?.defineLimite?.protestos?.ultimas_ocorrencias
                              ?.protesto,
                          ]
                    }
                  />
                ) : (
                  <NoOcurrence />
                )}
              </div>

              <div>
                <TitleStripe className="mb-5">Protesto Nacional</TitleStripe>
                {!!data?.defineLimite?.protestos?.total_protestos ? (
                  <Protestos
                    quantidadeTotal={
                      data?.protestoNacional?.resumoConsulta?.protestos
                        ?.quantidadeTotal
                    }
                    valorTotal={
                      data?.protestoNacional?.resumoConsulta?.protestos
                        ?.valorTotal
                    }
                    protestos={data?.protestoNacional?.ocorrencias}
                  />
                ) : (
                  <NoOcurrence />
                )}
              </div>

              <div>
                <TitleStripe className="mb-5">Negativações</TitleStripe>
                {data?.defineLimite?.pendencias_restricoes?.ultimas_ocorrencias
                  ?.pendencia_restricao ? (
                  <>
                    <PendenciasFinanceiras
                      quantidadeTotal={
                        data?.defineLimite?.pendencias_restricoes
                          ?.total_pendencias
                      }
                      dataMaiorPendencia={
                        data?.defineLimite?.pendencias_restricoes?.data_maior
                      }
                      valorTotal={
                        data?.defineLimite?.pendencias_restricoes?.valor
                      }
                      pendenciasFinanceiras={
                        Array.isArray(
                          data?.defineLimite?.pendencias_restricoes
                            ?.ultimas_ocorrencias?.pendencia_restricao
                        )
                          ? data?.defineLimite?.pendencias_restricoes
                              ?.ultimas_ocorrencias?.pendencia_restricao
                          : [
                              ...[],
                              data?.defineLimite?.pendencias_restricoes
                                ?.ultimas_ocorrencias?.pendencia_restricao,
                            ]
                      }
                    />
                  </>
                ) : (
                  <NoOcurrence />
                )}
              </div>

              <div>
                <TitleStripe className="mb-5">Pendências Serasa</TitleStripe>

                {data?.crednet?.consultaCredito?.pendenciasFinanceiras?.length >
                0 ? (
                  <>
                    <PendenciasFinanceiras
                      quantidadeTotal={
                        data?.crednet?.consultaCredito?.resumoConsulta
                          ?.pendenciasFinanceiras?.quantidadeTotal
                      }
                      valorTotal={
                        data?.crednet?.consultaCredito?.resumoConsulta
                          ?.pendenciasFinanceiras?.valorTotal
                      }
                      pendenciasFinanceiras={
                        data?.crednet?.consultaCredito?.pendenciasFinanceiras
                      }
                    />
                  </>
                ) : (
                  <NoOcurrence />
                )}
              </div>

              <div>
                <TitleStripe className="mb-5">Pendências Quod</TitleStripe>

                {data?.quod?.pendenciasFinanceiras?.length > 0 ? (
                  <>
                    <PendenciasFinanceiras
                      quantidadeTotal={
                        data?.quod?.resumoConsulta?.pendenciasFinanceiras
                          ?.quantidadeTotal
                      }
                      valorTotal={
                        data?.quod?.resumoConsulta?.pendenciasFinanceiras
                          ?.valorTotal
                      }
                      pendenciasFinanceiras={data?.quod?.pendenciasFinanceiras}
                    />
                  </>
                ) : (
                  <NoOcurrence />
                )}
              </div>

              <div>
                <TitleStripe className="mb-5">Ações Cíveis</TitleStripe>
                {!!data?.defineLimite?.acoes_civeis?.total_acoes ? (
                  <>
                    <AcoesCiveis
                      quantidadeTotal={
                        data?.defineLimite?.acoes_civeis?.total_acoes
                      }
                      primeiroValor={
                        data?.defineLimite?.acoes_civeis?.valor_primeiro
                      }
                      valorTotal={data?.defineLimite?.acoes_civeis?.valor_total}
                      acoesCiveis={
                        Array.isArray(
                          data?.defineLimite?.acoes_civeis?.ultimas_ocorrencias
                            ?.acao_civel
                        )
                          ? data?.defineLimite?.acoes_civeis
                              ?.ultimas_ocorrencias?.acao_civel
                          : [
                              ...[],
                              data?.defineLimite?.acoes_civeis
                                ?.ultimas_ocorrencias?.acao_civel,
                            ]
                      }
                    />
                  </>
                ) : (
                  <NoOcurrence />
                )}
              </div>

              <div>
                <TitleStripe className="mb-5">
                  Falências/Recuperações Judiciais
                </TitleStripe>
                {data &&
                !data?.defineLimite?.falencias_recuperacao_judicial
                  ?.mensagem ? (
                  <FalenciaRecuperacaoJudicial
                    falenciaRecuperacaoJudicial={
                      Array.isArray(
                        data?.defineLimite?.falencias_recuperacao_judicial
                          ?.ultimas_ocorrencias?.falencia_recuperacao_judicial
                      )
                        ? data?.defineLimite?.falencias_recuperacao_judicial
                            ?.ultimas_ocorrencias?.falencia_recuperacao_judicial
                        : [
                            ...[],
                            data?.defineLimite?.falencias_recuperacao_judicial
                              ?.ultimas_ocorrencias
                              ?.falencia_recuperacao_judicial,
                          ]
                    }
                  />
                ) : (
                  <NoOcurrence />
                )}
              </div>

              <div>
                <TitleStripe className="mb-5">
                  Histórico de Consultas
                </TitleStripe>

                {data?.defineLimite?.consultas?.ultimas_consultas?.consulta
                  ?.length > 0 ? (
                  <>
                    <HistoricoConsultas
                      quantidadeTotal={
                        data?.defineLimite?.consultas?.quantidade_total
                      }
                      periodoInicial={
                        data?.defineLimite?.consultas?.periodo_inicial
                      }
                      periodoFinal={
                        data?.defineLimite?.consultas?.periodo_final
                      }
                      historicoConsultas={
                        Array.isArray(
                          data?.defineLimite?.consultas?.ultimas_consultas
                            ?.consulta
                        )
                          ? data?.defineLimite?.consultas?.ultimas_consultas?.consulta?.map(
                              (hist) => ({
                                data: hist.data,
                                empresa: hist.razao_social,
                              })
                            )
                          : [
                              ...[],
                              {
                                data: data?.defineLimite?.consultas
                                  ?.ultimas_consultas?.consulta?.data,
                                empresa:
                                  data?.defineLimite?.consultas
                                    ?.ultimas_consultas?.consulta?.razao_social,
                              },
                            ]
                      }
                    />
                  </>
                ) : (
                  <NoOcurrence />
                )}

                <div className="border border-gray-300 rounded-lg mt-3">
                  <Table striped>
                    <Table.Head>
                      {data?.defineLimite?.consultas?.consultas_mes?.map(
                        (consulta, index) => (
                          <Table.HeadCell
                            key={index}
                            className="text-center text-black px-1"
                            style={{ background: "#EBF5FF" }}
                          >
                            {formatDateToDDMM(consulta.mes_ano)}
                          </Table.HeadCell>
                        )
                      )}
                    </Table.Head>

                    <Table.Body className="divide-y">
                      <Table.Row className="bg-white dark:border-gray-700 dark:bg-gray-800">
                        {data?.defineLimite?.consultas?.consultas_mes?.map(
                          (consult, index) => (
                            <Table.Cell
                              className="font-medium text-gray-900 dark:text-white text-center text-black py-1 px-1"
                              key={index}
                            >
                              {consult.quantidade}
                            </Table.Cell>
                          )
                        )}
                      </Table.Row>
                    </Table.Body>
                  </Table>
                </div>
              </div>

              <FooterQuery hasHeader={hasHeaderPDF} />
            </Card>
          ) : (
            <QueryLoading />
          )}
        </div>
      )}
    </>
  );
};

export default CompletaPlusCNPJQuery;
